import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, Package, CreditCard, ShoppingBag, LogOut, Plus, Trash2, Pencil, X, Copy, Upload, ChevronDown, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const aud = (n) => `A$${Number(n).toFixed(2)}`;

const STATUS = ["pending", "paid", "shipped", "cancelled"];

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <p className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className="font-display font-black text-3xl mt-2">{value}</p>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg border border-neutral-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-neutral-300 px-3 py-2 mt-1 focus:border-[#FF5722] outline-none text-sm";
const labelCls = "font-mono-x text-[11px] uppercase tracking-[0.15em] text-neutral-500";

function ImageField({ value, onChange }) {
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <label className={labelCls}>Photo</label>
      <div className="flex gap-2 mt-1">
        <input className={inputCls + " mt-0"} placeholder="Paste image URL or upload →" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="img-url" />
        <label className="shrink-0 border border-neutral-300 px-3 py-2 text-sm flex items-center gap-1 cursor-pointer hover:border-[#FF5722]" data-testid="img-upload-label">
          <Upload size={14} /> Upload
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="img-upload-input" />
        </label>
      </div>
      {value && <img src={value} alt="preview" className="mt-2 w-24 h-24 object-cover border border-neutral-200 rounded" data-testid="img-preview" />}
    </div>
  );
}

function ProductForm({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial || { name: "", tagline: "", description: "", price: 0, image: "", stock: 0, featured: false, specs: [] });
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-3">
      {[["name", "Name"], ["tagline", "Tagline"]].map(([k, l]) => (
        <div key={k}><label className={labelCls}>{l}</label><input className={inputCls} value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`pf-${k}`} /></div>
      ))}
      <ImageField value={f.image} onChange={(v) => upd("image", v)} />
      <div><label className={labelCls}>Description</label><textarea className={inputCls} rows={2} value={f.description} onChange={(e) => upd("description", e.target.value)} data-testid="pf-description" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Price (AUD)</label><input type="number" className={inputCls} value={f.price} onChange={(e) => upd("price", parseFloat(e.target.value) || 0)} data-testid="pf-price" /></div>
        <div><label className={labelCls}>Stock</label><input type="number" className={inputCls} value={f.stock} onChange={(e) => upd("stock", parseInt(e.target.value) || 0)} data-testid="pf-stock" /></div>
      </div>
      <div><label className={labelCls}>Specs (comma separated)</label><input className={inputCls} value={(f.specs || []).join(", ")} onChange={(e) => upd("specs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="pf-specs" /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => upd("featured", e.target.checked)} data-testid="pf-featured" /> Featured (best seller)</label>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(f)} className="bg-[#FF5722] text-white px-5 py-2.5 text-sm font-medium flex-1" data-testid="pf-save">Save</button>
        <button onClick={onClose} className="border border-neutral-300 px-5 py-2.5 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function PlanForm({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial || { name: "", price: 0, period: "month", features: [], popular: false, image: "" });
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-3">
      <div><label className={labelCls}>Name</label><input className={inputCls} value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="plf-name" /></div>
      <ImageField value={f.image} onChange={(v) => upd("image", v)} />
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Price (AUD)</label><input type="number" className={inputCls} value={f.price} onChange={(e) => upd("price", parseFloat(e.target.value) || 0)} data-testid="plf-price" /></div>
        <div><label className={labelCls}>Period</label><input className={inputCls} value={f.period} onChange={(e) => upd("period", e.target.value)} data-testid="plf-period" /></div>
      </div>
      <div><label className={labelCls}>Features (comma separated)</label><textarea rows={3} className={inputCls} value={(f.features || []).join(", ")} onChange={(e) => upd("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="plf-features" /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.popular} onChange={(e) => upd("popular", e.target.checked)} data-testid="plf-popular" /> Most popular</label>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(f)} className="bg-[#FF5722] text-white px-5 py-2.5 text-sm font-medium flex-1" data-testid="plf-save">Save</button>
        <button onClick={onClose} className="border border-neutral-300 px-5 py-2.5 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState("orders");
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null); // {type, item}
  const [expanded, setExpanded] = useState(null); // order id

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); nav("/admin/login"); };

  const saveProduct = async (f) => {
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, f);
      else await api.post("/admin/products", f);
      toast.success("Product saved"); setModal(null); load();
    } catch { toast.error("Save failed"); }
  };
  const savePlan = async (f) => {
    try {
      if (f.id) await api.put(`/admin/plans/${f.id}`, f);
      else await api.post("/admin/plans", f);
      toast.success("Plan saved"); setModal(null); load();
    } catch { toast.error("Save failed"); }
  };
  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };
  const delPlan = async (id) => { await api.delete(`/admin/plans/${id}`); toast.success("Deleted"); load(); };
  const dupProduct = async (p) => {
    const { id, ...rest } = p;
    try { await api.post("/admin/products", { ...rest, name: `${p.name} (Copy)` }); toast.success("Product duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };
  const dupPlan = async (p) => {
    const { id, ...rest } = p;
    try { await api.post("/admin/plans", { ...rest, name: `${p.name} (Copy)` }); toast.success("Plan duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };
  const setOrderStatus = async (id, status) => { await api.patch(`/admin/orders/${id}`, { status }); toast.success("Order updated"); load(); };

  const printPackingSlip = (o) => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = o.items.map((i) => `<tr>
        <td>${esc(i.name)}${i.kind === "plan" ? ' <span class="muted">(subscription)</span>' : ""}</td>
        <td class="c">${i.qty}</td>
        <td class="r">${aud(i.price)}</td>
        <td class="r">${aud(i.price * i.qty)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Packing slip #${esc(String(o.id).slice(-6))}</title>
      <style>
        *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#0a0a0b;margin:40px;font-size:13px}
        h1{font-size:22px;margin:0} .brand{color:#FF5722;font-weight:800;letter-spacing:.5px}
        .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0F172A;padding-bottom:14px;margin-bottom:20px}
        .muted{color:#888} .box{background:#f5f5f3;padding:14px;margin:10px 0}
        .grid{display:flex;gap:40px;margin-bottom:20px} .grid>div{flex:1}
        .lbl{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:4px}
        table{width:100%;border-collapse:collapse;margin-top:8px} th,td{padding:8px;border-bottom:1px solid #e5e5e5;text-align:left}
        th{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888} .r{text-align:right} .c{text-align:center}
        .total td{font-weight:bold;font-size:15px;border-bottom:none} .foot{margin-top:40px;color:#999;font-size:11px}
        @media print{body{margin:16px}}
      </style></head><body>
      <div class="head">
        <div><h1><span class="brand">INDIANTV.BOX</span></h1><div class="muted">Packing slip</div></div>
        <div class="r"><div><strong>Order #${esc(String(o.id).slice(-6))}</strong></div>
          <div class="muted">${esc(new Date(o.created_at).toLocaleString())}</div>
          <div class="muted">Status: ${esc(o.status)} · ${o.payment_method === "bank_transfer" ? "Bank transfer" : "PayPal"}</div></div>
      </div>
      <div class="grid">
        <div><div class="lbl">Ship to</div><strong>${esc(o.customer_name)}</strong><br>${esc(o.address) || '<span class="muted">No address provided</span>'}</div>
        <div><div class="lbl">Contact</div>${esc(o.email) || ""}<br>${esc(o.phone) || '<span class="muted">No phone</span>'}</div>
      </div>
      <table><thead><tr><th>Item</th><th class="c">Qty</th><th class="r">Unit</th><th class="r">Line total</th></tr></thead>
        <tbody>${rows}<tr class="total"><td colspan="3" class="r">Total</td><td class="r">${aud(o.total)}</td></tr></tbody></table>
      ${o.notes ? `<div class="box"><div class="lbl">Order notes</div>${esc(o.notes)}</div>` : ""}
      <div class="foot">Indian TV Box · admin@indiantvbox.com.au · 0412 213 515</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Please allow pop-ups to print the packing slip"); return; }
    w.document.write(html); w.document.close();
  };

  const NAV = [
    { k: "orders", label: "Orders", icon: ShoppingBag },
    { k: "products", label: "Products", icon: Package },
    { k: "plans", label: "Plans", icon: CreditCard },
  ];

  return (
    <div className="min-h-screen flex bg-neutral-50">
      <aside className="w-16 lg:w-60 bg-[#0F172A] text-white flex flex-col shrink-0">
        <div className="h-16 flex items-center px-4 lg:px-6 border-b border-neutral-800">
          <span className="font-display font-black hidden lg:block">INDIANTV<span className="text-[#FF5722]">.</span>BOX</span>
          <LayoutGrid className="lg:hidden mx-auto" size={20} />
        </div>
        <nav className="flex-1 p-2 lg:p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.k} onClick={() => setView(n.k)} data-testid={`nav-${n.k}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${view === n.k ? "bg-[#FF5722] text-white" : "text-neutral-400 hover:text-white"}`}>
              <n.icon size={18} /> <span className="hidden lg:block">{n.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-3 px-4 lg:px-6 py-4 text-sm text-neutral-400 hover:text-white border-t border-neutral-800">
          <LogOut size={18} /> <span className="hidden lg:block">Logout</span>
        </button>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <header className="h-16 bg-white border-b border-neutral-200 flex items-center px-6 justify-between">
          <h1 className="font-display font-bold text-xl capitalize">{view}</h1>
          <span className="font-mono-x text-xs text-neutral-500">{user?.email}</span>
        </header>

        <div className="p-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <Stat label="Total Orders" value={stats.total_orders ?? 0} />
            <Stat label="Pending" value={stats.pending_orders ?? 0} />
            <Stat label="Revenue" value={aud(stats.revenue ?? 0)} />
            <Stat label="Products" value={stats.products ?? 0} />
            <Stat label="Plans" value={stats.plans ?? 0} />
          </div>

          {view === "orders" && (
            <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left font-mono-x text-[11px] uppercase tracking-[0.1em] text-neutral-500">
                  <tr>{["Customer", "Contact", "Items", "Total", "Method", "Status", "Date"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <Fragment key={o.id}>
                    <tr className="border-t border-neutral-100 hover:bg-neutral-50 align-top" data-testid={`order-row-${o.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`order-expand-${o.id}`}
                          className="flex items-start gap-2 text-left">
                          <ChevronDown size={15} className={`mt-0.5 shrink-0 text-neutral-400 transition-transform ${expanded === o.id ? "rotate-180" : ""}`} />
                          <span><span className="font-medium block">{o.customer_name}</span><span className="text-neutral-500 text-xs">#{String(o.id).slice(-6)}</span></span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600 max-w-[220px]" data-testid={`order-contact-${o.id}`}>
                        {o.email && <p><a href={`mailto:${o.email}`} className="text-[#FF5722] hover:underline break-all">{o.email}</a></p>}
                        {o.phone && <p><a href={`tel:${o.phone}`} className="hover:underline">{o.phone}</a></p>}
                        {o.address ? <p className="text-neutral-500 mt-0.5">{o.address}</p> : <p className="text-neutral-400 italic">No address</p>}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                      <td className="px-4 py-3 font-medium">{aud(o.total)}</td>
                      <td className="px-4 py-3 text-neutral-600">{o.payment_method === "bank_transfer" ? "Bank" : "PayPal"}</td>
                      <td className="px-4 py-3">
                        <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} data-testid={`order-status-${o.id}`}
                          className="border border-neutral-300 px-2 py-1 text-xs bg-white">
                          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-neutral-500 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                    {expanded === o.id && (
                      <tr className="border-t border-neutral-100 bg-neutral-50/60" data-testid={`order-detail-${o.id}`}>
                        <td colSpan={7} className="px-6 py-5">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-display font-bold text-sm">Order #{String(o.id).slice(-6)} details</h4>
                            <button onClick={() => printPackingSlip(o)} data-testid={`print-slip-${o.id}`}
                              className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2 text-xs font-medium hover:bg-[#1e293b]">
                              <Printer size={14} /> Print packing slip
                            </button>
                          </div>
                          <div className="overflow-x-auto bg-white border border-neutral-200">
                            <table className="w-full text-xs">
                              <thead className="bg-neutral-50 text-left font-mono-x text-[10px] uppercase tracking-[0.1em] text-neutral-500">
                                <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Unit</th><th className="px-3 py-2 text-right">Line total</th></tr>
                              </thead>
                              <tbody>
                                {o.items.map((i, idx) => (
                                  <tr key={idx} className="border-t border-neutral-100">
                                    <td className="px-3 py-2 font-medium">{i.name}</td>
                                    <td className="px-3 py-2 text-neutral-500 capitalize">{i.kind || "product"}</td>
                                    <td className="px-3 py-2 text-center">{i.qty}</td>
                                    <td className="px-3 py-2 text-right">{aud(i.price)}</td>
                                    <td className="px-3 py-2 text-right">{aud(i.price * i.qty)}</td>
                                  </tr>
                                ))}
                                <tr className="border-t border-neutral-200 font-bold">
                                  <td className="px-3 py-2" colSpan={4}>Total</td>
                                  <td className="px-3 py-2 text-right">{aud(o.total)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-3">
                            <p className="font-mono-x text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-1">Order notes</p>
                            {o.notes ? <p className="text-sm text-neutral-700 whitespace-pre-wrap" data-testid={`order-notes-${o.id}`}>{o.notes}</p>
                              : <p className="text-sm text-neutral-400 italic">No notes provided.</p>}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === "products" && (
            <div>
              <button onClick={() => setModal({ type: "product" })} data-testid="add-product-btn"
                className="mb-4 bg-[#FF5722] text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={16} /> Add product</button>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <div key={p.id} className="bg-white border border-neutral-200 p-4" data-testid={`admin-product-${p.id}`}>
                    <div className="flex gap-3">
                      {p.image && <img src={p.image} alt={p.name} className="w-16 h-16 object-cover" />}
                      <div className="flex-1"><p className="font-display font-bold">{p.name}</p><p className="text-sm text-neutral-500">{aud(p.price)} · stock {p.stock}</p></div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setModal({ type: "product", item: p })} data-testid={`edit-product-${p.id}`} className="flex-1 border border-neutral-300 py-2 text-sm flex items-center justify-center gap-1"><Pencil size={13} /> Edit</button>
                      <button onClick={() => dupProduct(p)} data-testid={`dup-product-${p.id}`} className="border border-neutral-300 py-2 px-3 text-sm flex items-center gap-1" title="Duplicate"><Copy size={14} /></button>
                      <button onClick={() => delProduct(p.id)} data-testid={`del-product-${p.id}`} className="border border-neutral-300 py-2 px-3 text-sm text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === "plans" && (
            <div>
              <button onClick={() => setModal({ type: "plan" })} data-testid="add-plan-btn"
                className="mb-4 bg-[#FF5722] text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={16} /> Add plan</button>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((p) => (
                  <div key={p.id} className="bg-white border border-neutral-200 p-4" data-testid={`admin-plan-${p.id}`}>
                    <div className="flex gap-3">
                      {p.image && <img src={p.image} alt={p.name} className="w-16 h-16 object-cover" />}
                      <div className="flex-1">
                        <p className="font-display font-bold">{p.name} {p.popular && <span className="text-[#FF5722] text-xs">★</span>}</p>
                        <p className="text-sm text-neutral-500">{aud(p.price)}/{p.period} · {p.features.length} features</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setModal({ type: "plan", item: p })} data-testid={`edit-plan-${p.id}`} className="flex-1 border border-neutral-300 py-2 text-sm flex items-center justify-center gap-1"><Pencil size={13} /> Edit</button>
                      <button onClick={() => dupPlan(p)} data-testid={`dup-plan-${p.id}`} className="border border-neutral-300 py-2 px-3 text-sm flex items-center gap-1" title="Duplicate"><Copy size={14} /></button>
                      <button onClick={() => delPlan(p.id)} data-testid={`del-plan-${p.id}`} className="border border-neutral-300 py-2 px-3 text-sm text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {modal?.type === "product" && (
        <Modal title={modal.item ? "Edit product" : "New product"} onClose={() => setModal(null)}>
          <ProductForm initial={modal.item} onSave={saveProduct} onClose={() => setModal(null)} />
        </Modal>
      )}
      {modal?.type === "plan" && (
        <Modal title={modal.item ? "Edit plan" : "New plan"} onClose={() => setModal(null)}>
          <PlanForm initial={modal.item} onSave={savePlan} onClose={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
}
